import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LogOut,
    Inbox,
    CheckCheck,
    Archive,
    Trash2,
    RefreshCw,
    Mail,
    Phone,
    Calendar,
    Users,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
    const map = {
        new: "bg-secondary text-white",
        read: "bg-accent text-white",
        archived: "bg-muted text-muted-foreground border border-border",
    };
    return (
        <span
            className={`overline text-[9px] px-2.5 py-1 ${map[status] || map.new}`}
        >
            {status}
        </span>
    );
};

const AdminDashboard = () => {
    const { admin, logout, loading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!loading && !admin) {
            navigate("/admin/login");
        }
    }, [loading, admin, navigate]);

    const fetchItems = async () => {
        setFetching(true);
        try {
            const r = await api.get("/inquiries");
            setItems(r.data);
        } catch {
            toast.error("Failed to load enquiries.");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (admin) fetchItems();
    }, [admin]);

    const updateStatus = async (id, status) => {
        try {
            const r = await api.patch(`/inquiries/${id}`, { status });
            setItems((prev) => prev.map((i) => (i.id === id ? r.data : i)));
            if (selected?.id === id) setSelected(r.data);
            toast.success(`Marked ${status}.`);
        } catch {
            toast.error("Update failed.");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this enquiry?")) return;
        try {
            await api.delete(`/inquiries/${id}`);
            setItems((prev) => prev.filter((i) => i.id !== id));
            if (selected?.id === id) setSelected(null);
            toast.success("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    const stats = {
        total: items.length,
        new: items.filter((i) => i.status === "new").length,
        read: items.filter((i) => i.status === "read").length,
        archived: items.filter((i) => i.status === "archived").length,
    };

    if (loading || !admin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="overline text-muted-foreground">Loading…</div>
            </div>
        );
    }

    return (
        <div
            data-testid="admin-dashboard-page"
            className="min-h-screen bg-background pt-24 pb-16"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-border">
                    <div>
                        <div className="overline text-secondary mb-2">
                            Dashboard
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-light text-primary tracking-tight">
                            Booking Enquiries
                        </h1>
                        <div className="mt-2 text-sm text-muted-foreground">
                            Signed in as{" "}
                            <span className="text-foreground">
                                {admin.email}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="admin-refresh"
                            onClick={fetchItems}
                            className="inline-flex items-center gap-2 px-5 py-2.5 border border-border text-xs tracking-[0.2em] uppercase hover:border-primary transition-colors"
                        >
                            <RefreshCw size={14} /> Refresh
                        </button>
                        <button
                            data-testid="admin-logout"
                            onClick={() => {
                                logout();
                                navigate("/admin/login");
                            }}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-xs tracking-[0.2em] uppercase hover:bg-secondary transition-colors"
                        >
                            <LogOut size={14} /> Sign Out
                        </button>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
                    {[
                        { label: "Total", value: stats.total, icon: Inbox },
                        { label: "New", value: stats.new, icon: Mail },
                        { label: "Read", value: stats.read, icon: CheckCheck },
                        {
                            label: "Archived",
                            value: stats.archived,
                            icon: Archive,
                        },
                    ].map((s) => (
                        <div
                            key={s.label}
                            className="bg-background p-6 flex items-center justify-between"
                        >
                            <div>
                                <div className="overline text-muted-foreground">
                                    {s.label}
                                </div>
                                <div className="font-display text-4xl text-primary mt-1">
                                    {s.value}
                                </div>
                            </div>
                            <s.icon
                                size={22}
                                strokeWidth={1.25}
                                className="text-secondary"
                            />
                        </div>
                    ))}
                </div>

                <div className="mt-10 grid lg:grid-cols-12 gap-0 border border-border">
                    <div className="lg:col-span-5 border-r border-border max-h-[70vh] overflow-y-auto">
                        {fetching ? (
                            <div className="p-10 overline text-muted-foreground">
                                Loading…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="p-10 text-center">
                                <Inbox
                                    size={28}
                                    strokeWidth={1.25}
                                    className="text-muted-foreground mx-auto"
                                />
                                <div className="mt-4 font-display text-xl text-primary">
                                    No enquiries yet
                                </div>
                                <div className="mt-1 text-sm text-muted-foreground font-light">
                                    When guests enquire, they'll appear here.
                                </div>
                            </div>
                        ) : (
                            items.map((i) => (
                                <button
                                    key={i.id}
                                    data-testid={`inquiry-item-${i.id}`}
                                    onClick={() => setSelected(i)}
                                    className={`w-full text-left p-6 border-b border-border transition-colors ${
                                        selected?.id === i.id
                                            ? "bg-muted"
                                            : "hover:bg-muted/50"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="font-display text-xl text-primary">
                                            {i.name}
                                        </div>
                                        <StatusBadge status={i.status} />
                                    </div>
                                    <div className="mt-1 text-sm text-muted-foreground">
                                        {i.email}
                                    </div>
                                    <div className="mt-3 text-sm text-foreground line-clamp-2 font-light">
                                        {i.message}
                                    </div>
                                    <div className="mt-3 overline text-[9px] text-muted-foreground">
                                        {new Date(i.created_at).toLocaleString()}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                    <div className="lg:col-span-7 p-8 lg:p-12 min-h-[40vh]">
                        {selected ? (
                            <div data-testid="inquiry-detail">
                                <div className="flex items-start justify-between gap-4 pb-6 border-b border-border">
                                    <div>
                                        <div className="overline text-secondary mb-2">
                                            Enquiry
                                        </div>
                                        <h2 className="font-display text-3xl text-primary">
                                            {selected.name}
                                        </h2>
                                        <div className="mt-1 text-sm text-muted-foreground">
                                            {new Date(
                                                selected.created_at
                                            ).toLocaleString()}
                                        </div>
                                    </div>
                                    <StatusBadge status={selected.status} />
                                </div>

                                <div className="mt-6 grid sm:grid-cols-2 gap-5 text-sm">
                                    <div className="flex items-center gap-3">
                                        <Mail
                                            size={14}
                                            className="text-secondary"
                                        />
                                        <a
                                            href={`mailto:${selected.email}`}
                                            className="text-foreground hover:text-secondary"
                                        >
                                            {selected.email}
                                        </a>
                                    </div>
                                    {selected.phone && (
                                        <div className="flex items-center gap-3">
                                            <Phone
                                                size={14}
                                                className="text-secondary"
                                            />
                                            <span className="text-foreground">
                                                {selected.phone}
                                            </span>
                                        </div>
                                    )}
                                    {(selected.check_in ||
                                        selected.check_out) && (
                                        <div className="flex items-center gap-3">
                                            <Calendar
                                                size={14}
                                                className="text-secondary"
                                            />
                                            <span className="text-foreground">
                                                {selected.check_in || "?"} →{" "}
                                                {selected.check_out || "?"}
                                            </span>
                                        </div>
                                    )}
                                    {selected.guests && (
                                        <div className="flex items-center gap-3">
                                            <Users
                                                size={14}
                                                className="text-secondary"
                                            />
                                            <span className="text-foreground">
                                                {selected.guests} guests
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-8">
                                    <div className="overline text-muted-foreground mb-3">
                                        Message
                                    </div>
                                    <p className="text-foreground leading-relaxed whitespace-pre-wrap font-light">
                                        {selected.message}
                                    </p>
                                </div>

                                <div className="mt-10 pt-6 border-t border-border flex flex-wrap gap-3">
                                    <button
                                        data-testid="mark-read-btn"
                                        onClick={() =>
                                            updateStatus(selected.id, "read")
                                        }
                                        className="inline-flex items-center gap-2 px-5 py-2.5 border border-border text-xs tracking-[0.2em] uppercase hover:border-primary transition-colors"
                                    >
                                        <CheckCheck size={14} /> Mark Read
                                    </button>
                                    <button
                                        data-testid="archive-btn"
                                        onClick={() =>
                                            updateStatus(
                                                selected.id,
                                                "archived"
                                            )
                                        }
                                        className="inline-flex items-center gap-2 px-5 py-2.5 border border-border text-xs tracking-[0.2em] uppercase hover:border-primary transition-colors"
                                    >
                                        <Archive size={14} /> Archive
                                    </button>
                                    <a
                                        data-testid="reply-btn"
                                        href={`mailto:${selected.email}?subject=Re: Villa Alam Lembongan Enquiry`}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-xs tracking-[0.2em] uppercase hover:bg-secondary transition-colors"
                                    >
                                        <Mail size={14} /> Reply by Email
                                    </a>
                                    <button
                                        data-testid="delete-btn"
                                        onClick={() => remove(selected.id)}
                                        className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.2em] uppercase text-destructive hover:text-white hover:bg-destructive transition-colors border border-destructive"
                                    >
                                        <Trash2 size={14} /> Delete
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex items-center justify-center text-center">
                                <div>
                                    <Inbox
                                        size={32}
                                        strokeWidth={1.25}
                                        className="text-muted-foreground mx-auto"
                                    />
                                    <div className="mt-4 font-display text-2xl text-primary">
                                        Select an enquiry
                                    </div>
                                    <div className="mt-2 text-sm text-muted-foreground font-light">
                                        Pick one from the list to view details.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
